import { useState, useEffect, useCallback } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { Card, CardContent, CardDescription, CardHeader, CardTitle, CardFooter } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Progress } from '../components/ui/progress';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from '../components/ui/dialog';
import { ScrollArea } from '../components/ui/scroll-area';
import {
  CheckCircle2, Sparkles, Zap, Crown, Loader2,
  ArrowRight, Clock, FileText, CreditCard, XCircle,
  RefreshCw, ExternalLink, AlertTriangle, Receipt, CalendarClock
} from 'lucide-react';
import { toast } from 'sonner';

export const SubscriptionPage = () => {
  const { api, user, refreshUser } = useAuth();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [tiers, setTiers] = useState([]);
  const [currentSubscription, setCurrentSubscription] = useState(null);
  const [invoices, setInvoices] = useState([]);
  const [loading, setLoading] = useState(true);
  const [processingTier, setProcessingTier] = useState(null);
  const [checkingPayment, setCheckingPayment] = useState(false);
  const [cancelModalOpen, setCancelModalOpen] = useState(false);
  const [cancelling, setCancelling] = useState(false);
  const [reactivating, setReactivating] = useState(false);

  useEffect(() => {
    fetchData();
    
    // Check for successful payment
    const sessionId = searchParams.get('session_id');
    if (sessionId) {
      checkPaymentStatus(sessionId);
    }
  }, []);

  const fetchData = async () => {
    try {
      const [tiersRes, subRes, invoicesRes] = await Promise.all([
        api.get('/subscription/tiers'),
        api.get('/subscription/current'),
        api.get('/subscription/invoices').catch(() => ({ data: { invoices: [] } }))
      ]);
      setTiers(tiersRes.data.tiers);
      setCurrentSubscription(subRes.data);
      setInvoices(invoicesRes.data.invoices || []);
    } catch (error) {
      toast.error('Failed to load subscription data');
    } finally {
      setLoading(false);
    }
  };

  const checkPaymentStatus = async (sessionId) => {
    setCheckingPayment(true);
    let attempts = 0;
    const maxAttempts = 10;

    const poll = async () => {
      try {
        const response = await api.get(`/subscription/checkout/status/${sessionId}`);
        
        if (response.data.payment_status === 'paid') {
          toast.success('Payment successful! Your subscription is now active.');
          navigate('/subscription', { replace: true });
          fetchData();
          setCheckingPayment(false);
          return;
        } else if (response.data.status === 'expired') {
          toast.error('Payment session expired');
          setCheckingPayment(false);
          return;
        }

        attempts++;
        if (attempts < maxAttempts) {
          setTimeout(poll, 2000);
        } else {
          setCheckingPayment(false);
          toast.info('Payment is being processed. Please refresh to check status.');
        }
      } catch (error) {
        setCheckingPayment(false);
        toast.error('Error checking payment status');
      }
    };

    poll();
  };

  const handleSubscribe = async (tierId) => {
    setProcessingTier(tierId);
    try {
      const response = await api.post('/subscription/checkout', {
        tier_id: tierId,
        origin_url: window.location.origin
      });
      
      // Redirect to Stripe checkout
      window.location.href = response.data.checkout_url;
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to start checkout');
      setProcessingTier(null);
    }
  };

  const handleCancel = async () => {
    setCancelling(true);
    try {
      const response = await api.post('/subscription/cancel');
      toast.success(response.data.message);
      setCancelModalOpen(false);
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to cancel subscription');
    } finally {
      setCancelling(false);
    }
  };

  const handleReactivate = async () => {
    setReactivating(true);
    try {
      const response = await api.post('/subscription/reactivate');
      toast.success(response.data.message);
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to reactivate subscription');
    } finally {
      setReactivating(false);
    }
  };

  const handleChangeTier = async (tierId) => {
    if (!currentSubscription?.stripe_subscription_id) {
      // No existing subscription, go to checkout
      handleSubscribe(tierId);
      return;
    }
    // Confirm with direction-aware copy so users know what's happening
    const targetTier = tiers.find((t) => t.id === tierId);
    const currentTierObj = tiers.find((t) => t.id === currentSubscription?.tier?.id);
    const isDowngrade =
      targetTier && currentTierObj && Number(targetTier.price) < Number(currentTierObj.price);
    const renewDate = currentSubscription?.end_date
      ? new Date(currentSubscription.end_date).toLocaleDateString()
      : 'your next renewal date';
    const confirmMsg = isDowngrade
      ? `Downgrade to ${targetTier?.name}?\n\nYou'll keep your current ${currentTierObj?.name} features ` +
        `until ${renewDate}, then automatically move to ${targetTier?.name}.\n\nNo charge today.`
      : `Upgrade to ${targetTier?.name}?\n\nYou'll be charged a prorated amount today for the remainder ` +
        `of this billing cycle and unlock the new limits immediately.`;
    if (!window.confirm(confirmMsg)) return;

    setProcessingTier(tierId);
    try {
      const response = await api.post('/subscription/change', {
        tier_id: tierId,
        origin_url: window.location.origin
      });
      toast.success(response.data.message);
      // Refresh AuthContext so the new tier / pending_tier_change shows up app-wide
      await refreshUser();
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to change subscription');
    } finally {
      setProcessingTier(null);
    }
  };

  const handleCancelPendingChange = useCallback(async () => {
    try {
      const r = await api.post('/subscription/cancel-pending-change');
      toast.success(r.data?.message || 'Scheduled downgrade cancelled.');
      await refreshUser();
      fetchData();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Failed to cancel scheduled change.');
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [api]);

  const getTierIcon = (tierId) => {
    const icons = {
      free: <Clock className="w-6 h-6" />,
      free_trial: <Clock className="w-6 h-6" />,
      pro: <Zap className="w-6 h-6" />,
      enterprise: <Crown className="w-6 h-6" />
    };
    return icons[tierId] || <Sparkles className="w-6 h-6" />;
  };

  const getTierColor = (tierId) => {
    const colors = {
      free: 'bg-zinc-100 text-zinc-600',
      free_trial: 'bg-zinc-100 text-zinc-600',
      pro: 'bg-indigo-100 text-indigo-600',
      enterprise: 'bg-amber-100 text-amber-600'
    };
    return colors[tierId] || 'bg-zinc-100 text-zinc-600';
  };

  const getStatusBadge = (status) => {
    const badges = {
      active: <Badge className="bg-emerald-100 text-emerald-700">Active</Badge>,
      cancelling: <Badge className="bg-amber-100 text-amber-700">Cancelling</Badge>,
      cancelled: <Badge variant="destructive">Cancelled</Badge>,
      past_due: <Badge variant="destructive">Past Due</Badge>,
      expired: <Badge variant="secondary">Expired</Badge>
    };
    return badges[status] || <Badge variant="secondary">{status}</Badge>;
  };

  if (loading || checkingPayment) {
    return (
      <div className="flex items-center justify-center min-h-[60vh]">
        <div className="text-center">
          <Loader2 className="w-8 h-8 animate-spin text-indigo-600 mx-auto mb-4" />
          <p className="text-zinc-600">
            {checkingPayment ? 'Processing your payment...' : 'Loading subscription...'}
          </p>
        </div>
      </div>
    );
  }

  const currentTier = currentSubscription?.tier;
  const postsUsed = currentSubscription?.posts_used || 0;
  const postsLimit = currentSubscription?.posts_limit || 0;
  const usagePercent = postsLimit > 0 ? Math.min((postsUsed / postsLimit) * 100, 100) : 0;
  const isCancelling = currentSubscription?.status === 'cancelling';
  const hasActiveSubscription = currentSubscription?.stripe_subscription_id && currentSubscription?.status !== 'cancelled';

  return (
    <div className="space-y-6" data-testid="subscription-page">
      {/* Promo trial banner — visible while inside the $1 / N-day window. */}
      {currentSubscription?.is_promo_trial && (
        <Card className="border-amber-300 bg-amber-50" data-testid="promo-trial-banner">
          <CardContent className="p-4 space-y-2">
            <div className="flex items-start gap-3">
              <CalendarClock className="w-5 h-5 text-amber-700 flex-shrink-0 mt-0.5" />
              <div className="text-sm text-amber-900 flex-1">
                <div className="font-semibold">
                  You're on the introductory trial.
                </div>
                <div className="text-xs text-amber-800 mt-0.5">
                  {currentSubscription.promo_trial_ends_at && (
                    <>Stripe will auto-charge the full plan price on{' '}
                    <b>{new Date(currentSubscription.promo_trial_ends_at).toLocaleDateString()}</b>.{' '}</>
                  )}
                  Cancel anytime in Subscription before then to avoid the charge — any posts scheduled past that date will pause automatically.
                </div>
              </div>
            </div>
            {currentSubscription.promo_trial_post_cap != null && (
              <div className="text-xs text-amber-900 pl-8" data-testid="promo-trial-cap-meter">
                Trial post usage:{' '}
                <b>{currentSubscription.promo_trial_posts_used ?? 0}</b>
                {' / '}
                <b>{currentSubscription.promo_trial_post_cap}</b> generated
              </div>
            )}
          </CardContent>
        </Card>
      )}
      {/* Pending downgrade banner — visible until renewal applies the change. */}
      {user?.pending_tier_change?.tier_id && (
        <Card
          className="border-amber-200 bg-amber-50"
          data-testid="pending-downgrade-banner"
        >
          <CardContent className="p-4 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
            <div className="flex items-start gap-3">
              <CalendarClock className="w-5 h-5 text-amber-700 flex-shrink-0 mt-0.5" />
              <div className="text-sm text-amber-900">
                <div className="font-semibold">
                  Downgrading to {user.pending_tier_change.tier_name} on{' '}
                  {user.pending_tier_change.effective_at
                    ? new Date(user.pending_tier_change.effective_at).toLocaleDateString()
                    : 'next renewal'}
                </div>
                <div className="text-xs text-amber-800 mt-0.5">
                  You keep your current plan's features and limits until then. No charge today.
                </div>
              </div>
            </div>
            <Button
              variant="outline"
              size="sm"
              onClick={handleCancelPendingChange}
              className="border-amber-300 text-amber-900 hover:bg-amber-100"
              data-testid="cancel-pending-downgrade-btn"
            >
              Cancel downgrade
            </Button>
          </CardContent>
        </Card>
      )}

      {/* Current Plan */}
      <Card className="border-zinc-200 bg-gradient-to-r from-indigo-50 to-violet-50">
        <CardHeader>
          <div className="flex items-center justify-between">
            <div>
              <CardTitle className="text-xl">Current Plan</CardTitle>
              <CardDescription>Your subscription details</CardDescription>
            </div>
            <div className="flex items-center gap-2">
              {currentTier && (
                <Badge className={getTierColor(currentTier.id)}>
                  {currentTier.name}
                </Badge>
              )}
              {getStatusBadge(currentSubscription?.status)}
            </div>
          </div>
        </CardHeader>
        <CardContent>
          <div className="grid md:grid-cols-4 gap-6">
            <div>
              <p className="text-sm text-zinc-500 mb-1">Status</p>
              <p className="font-semibold text-zinc-900 capitalize flex items-center gap-2">
                {currentSubscription?.status || 'Active'}
                {currentSubscription?.auto_renew && hasActiveSubscription && (
                  <RefreshCw className="w-4 h-4 text-emerald-500" title="Auto-renewing" />
                )}
              </p>
            </div>
            <div>
              <p className="text-sm text-zinc-500 mb-1">
                {currentTier?.is_trial ? 'Trial Ends' : isCancelling ? 'Access Until' : 'Renews'}
              </p>
              <p className="font-semibold text-zinc-900">
                {currentSubscription?.end_date 
                  ? new Date(currentSubscription.end_date).toLocaleDateString()
                  : 'N/A'}
              </p>
            </div>
            <div>
              <p className="text-sm text-zinc-500 mb-1">Posts This Month</p>
              <div className="flex items-center gap-2">
                <span className="font-semibold text-zinc-900">
                  {postsUsed} / {postsLimit === -1 ? '∞' : postsLimit}
                </span>
              </div>
              {postsLimit > 0 && (
                <Progress value={usagePercent} className="h-2 mt-2" />
              )}
            </div>
            <div>
              <p className="text-sm text-zinc-500 mb-1">Billing</p>
              {hasActiveSubscription ? (
                <div className="flex gap-2">
                  {isCancelling ? (
                    <Button 
                      size="sm" 
                      onClick={handleReactivate}
                      disabled={reactivating}
                      className="bg-emerald-600 hover:bg-emerald-700"
                      data-testid="reactivate-btn"
                    >
                      {reactivating && <Loader2 className="w-3 h-3 animate-spin mr-1" />}
                      Reactivate
                    </Button>
                  ) : (
                    <Button 
                      size="sm" 
                      variant="outline"
                      onClick={() => setCancelModalOpen(true)}
                      data-testid="cancel-btn"
                    >
                      Cancel Plan
                    </Button>
                  )}
                </div>
              ) : (
                <p className="text-sm text-zinc-600">No active subscription</p>
              )}
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Cancelling Warning */}
      {isCancelling && (
        <Card className="border-amber-200 bg-amber-50">
          <CardContent className="p-4 flex items-start gap-3">
            <AlertTriangle className="w-5 h-5 text-amber-600 flex-shrink-0 mt-0.5" />
            <div>
              <p className="font-medium text-amber-900">Your subscription is set to cancel</p>
              <p className="text-sm text-amber-700">
                You'll keep access until {new Date(currentSubscription.end_date).toLocaleDateString()}. 
                Click "Reactivate" to continue your subscription.
              </p>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Subscription Tiers */}
      <div>
        <h2 className="text-xl font-bold tracking-tight text-zinc-900 mb-4">
          {currentTier?.is_trial ? 'Upgrade Your Plan' : 'Available Plans'}
        </h2>
        <div className="grid md:grid-cols-3 gap-6">
          {tiers.map((tier) => {
            const isCurrentTier = currentTier?.id === tier.id;
            const isUpgrade = !tier.is_trial && (currentTier?.is_trial || tier.price > (currentTier?.price || 0));
            const isDowngrade = !tier.is_trial && !currentTier?.is_trial && tier.price < (currentTier?.price || 0);
            
            return (
              <Card 
                key={tier.id}
                className={`border-2 transition-all ${
                  isCurrentTier 
                    ? 'border-indigo-500 shadow-lg' 
                    : 'border-zinc-200 hover:border-zinc-300'
                }`}
                data-testid={`tier-${tier.id}`}
              >
                <CardHeader>
                  <div className={`w-12 h-12 rounded-xl ${getTierColor(tier.id)} flex items-center justify-center mb-2`}>
                    {getTierIcon(tier.id)}
                  </div>
                  <CardTitle className="flex items-center justify-between">
                    {tier.name}
                    {isCurrentTier && (
                      <Badge variant="outline" className="border-indigo-500 text-indigo-600">
                        Current
                      </Badge>
                    )}
                  </CardTitle>
                  <div className="flex items-baseline gap-1">
                    {tier.is_trial ? (
                      <span className="text-3xl font-bold text-zinc-900">Free</span>
                    ) : tier.discount_active && tier.discounted_price != null ? (
                      <div className="flex flex-col">
                        <div className="flex items-baseline gap-2">
                          <span className="text-sm text-zinc-400 line-through">${tier.price}</span>
                          <span className="text-3xl font-bold text-zinc-900">
                            ${Number(tier.discounted_price).toFixed(2)}
                          </span>
                          <span className="text-zinc-500">/month</span>
                        </div>
                        <span className="text-[11px] text-emerald-700 font-semibold mt-0.5">
                          Save {Math.round(tier.discount_percent)}%
                          {tier.discount_label ? ` — ${tier.discount_label}` : ' — limited time'}
                        </span>
                      </div>
                    ) : (
                      <>
                        <span className="text-3xl font-bold text-zinc-900">${tier.price}</span>
                        <span className="text-zinc-500">/month</span>
                      </>
                    )}
                  </div>
                </CardHeader>
                <CardContent>
                  <ul className="space-y-3">
                    {tier.features?.map((feature, i) => (
                      <li key={i} className="flex items-start gap-2">
                        <CheckCircle2 className="w-5 h-5 text-emerald-500 flex-shrink-0 mt-0.5" />
                        <span className="text-sm text-zinc-600">{feature}</span>
                      </li>
                    ))}
                    {!tier.is_trial && (
                      <li className="flex items-start gap-2">
                        <RefreshCw className="w-5 h-5 text-indigo-500 flex-shrink-0 mt-0.5" />
                        <span className="text-sm text-zinc-600">Auto-renews monthly</span>
                      </li>
                    )}
                  </ul>
                </CardContent>
                <CardFooter>
                  {isCurrentTier ? (
                    <Button className="w-full" disabled variant="outline">
                      Current Plan
                    </Button>
                  ) : tier.is_trial ? (
                    <Button className="w-full" disabled variant="outline">
                      Trial Plan
                    </Button>
                  ) : (
                    <Button 
                      className={`w-full ${isDowngrade ? 'bg-zinc-600 hover:bg-zinc-700' : 'bg-indigo-600 hover:bg-indigo-700'}`}
                      onClick={() => hasActiveSubscription ? handleChangeTier(tier.id) : handleSubscribe(tier.id)}
                      disabled={processingTier === tier.id}
                      data-testid={`subscribe-${tier.id}`}
                    >
                      {processingTier === tier.id ? (
                        <Loader2 className="w-4 h-4 animate-spin mr-2" />
                      ) : (
                        <ArrowRight className="w-4 h-4 mr-2" />
                      )}
                      {isUpgrade
                        ? 'Upgrade now'
                        : isDowngrade
                          ? 'Downgrade at renewal'
                          : 'Subscribe'}
                    </Button>
                  )}
                </CardFooter>
              </Card>
            );
          })}
        </div>
      </div>

      {/* Invoice History */}
      {invoices.length > 0 && (
        <Card className="border-zinc-200">
          <CardHeader>
            <CardTitle className="text-lg flex items-center gap-2">
              <Receipt className="w-5 h-5" />
              Invoice History
            </CardTitle>
          </CardHeader>
          <CardContent>
            <ScrollArea className="h-[200px]">
              <div className="space-y-2">
                {invoices.map((invoice) => (
                  <div 
                    key={invoice.id}
                    className="flex items-center justify-between p-3 rounded-lg bg-zinc-50"
                  >
                    <div className="flex items-center gap-3">
                      <CreditCard className="w-4 h-4 text-zinc-400" />
                      <div>
                        <p className="font-medium text-zinc-900">
                          ${invoice.amount_paid.toFixed(2)} {invoice.currency.toUpperCase()}
                        </p>
                        <p className="text-sm text-zinc-500">
                          {new Date(invoice.created).toLocaleDateString()}
                        </p>
                      </div>
                    </div>
                    <div className="flex items-center gap-2">
                      <Badge variant={invoice.status === 'paid' ? 'default' : 'secondary'} className={invoice.status === 'paid' ? 'bg-emerald-100 text-emerald-700' : ''}>
                        {invoice.status}
                      </Badge>
                      {invoice.hosted_invoice_url && (
                        <Button 
                          variant="ghost" 
                          size="sm"
                          onClick={() => window.open(invoice.hosted_invoice_url, '_blank')}
                        >
                          <ExternalLink className="w-4 h-4" />
                        </Button>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            </ScrollArea>
          </CardContent>
        </Card>
      )}

      {/* Cancel Confirmation Modal */}
      <Dialog open={cancelModalOpen} onOpenChange={setCancelModalOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <XCircle className="w-5 h-5 text-red-500" />
              Cancel Subscription
            </DialogTitle>
            <DialogDescription>
              Are you sure you want to cancel your subscription?
            </DialogDescription>
          </DialogHeader>
          <div className="py-4">
            <p className="text-sm text-zinc-600 mb-4">
              Your subscription will remain active until the end of your current billing period 
              ({currentSubscription?.end_date ? new Date(currentSubscription.end_date).toLocaleDateString() : 'N/A'}).
            </p>
            <p className="text-sm text-zinc-600">
              After that, you'll be moved to the free plan (5 posts + 5 AI images / month). You can reactivate anytime before the period ends.
            </p>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setCancelModalOpen(false)}>
              Keep Subscription
            </Button>
            <Button 
              variant="destructive" 
              onClick={handleCancel}
              disabled={cancelling}
              data-testid="confirm-cancel-btn"
            >
              {cancelling && <Loader2 className="w-4 h-4 animate-spin mr-2" />}
              Cancel Subscription
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};
